import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Target, Users, Award, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To make quality home services accessible, affordable, and reliable for every Indian household.'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'We put our customers at the heart of everything we do, ensuring exceptional service every time.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in every interaction, continuously improving our services and standards.'
    },
    {
      icon: Heart,
      title: 'Trust & Safety',
      description: 'Building trust through thorough verification, transparent pricing, and consistent quality.'
    }
  ];

  const team = [
    { name: 'Rahul Sharma', role: 'Founder & CEO', initial: 'R' },
    { name: 'Priya Patel', role: 'Head of Operations', initial: 'P' },
    { name: 'Amit Kumar', role: 'CTO', initial: 'A' },
    { name: 'Sneha Desai', role: 'Head of Customer Success', initial: 'S' }
  ];

  const milestones = [
    { year: '2020', event: 'Maidly Founded', description: 'Started with a vision to transform home services' },
    { year: '2021', event: '10,000 Customers', description: 'Reached our first major milestone' },
    { year: '2022', event: '5 Cities', description: 'Expanded to 5 major Indian cities' },
    { year: '2023', event: '50,000+ Services', description: 'Completed over 50,000 successful bookings' },
    { year: '2024', event: 'Series A Funding', description: 'Raised funding to accelerate growth' },
    { year: '2025', event: 'National Presence', description: 'Serving customers across India' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-teal-50 to-green-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Maidly
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Maidly is a household services platform connecting homeowners and maids, helping both find domestic help and work opportunities quickly and easily.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8">
                About Us
              </h2>
              <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed space-y-6">
                <p>
                  Maidly is a leading on-demand household services platform and job management system designed to streamline connections between homeowners and professional cleaners. Our platform provides an efficient, all-in-one solution for managing domestic tasks and household staff. We act as a bridge between homeowners who need trusted household workers and maids or domestic workers looking for suitable employment.
                </p>
                <p>
                  Our platform connects homeowners with skilled domestic workers such as maids, cleaners, cooks, babysitters, and caregivers. At the same time, Maidly empowers maids and household workers by helping them connect directly with families who need their services, creating fair and transparent opportunities for both sides.
                </p>
                <p>
                  We understand how challenging it can be to find dependable household help or consistent work. Maidly was built to reduce this gap by providing a platform where homeowners and workers can easily find, connect, and communicate with each other based on their needs.
                </p>
                <p>
                  Our goal is to simplify household management while supporting domestic workers with better visibility, flexibility, and opportunities. Maidly believes in building trust, saving time, and creating meaningful connections that make everyday life easier for families and workers alike.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-2"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-100 to-green-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-teal-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600">
              Key milestones in our growth
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative pl-8 pb-12 last:pb-0 border-l-2 border-teal-200"
              >
                <div className="absolute left-0 top-0 w-4 h-4 bg-teal-600 rounded-full transform -translate-x-[9px]"></div>
                <div className="bg-gray-50 rounded-2xl p-6">
                  <span className="inline-block px-4 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-3">
                    {milestone.year}
                  </span>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {milestone.event}
                  </h3>
                  <p className="text-gray-600">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Leadership Team
            </h2>
            <p className="text-xl text-gray-600">
              Meet the people behind Maidly
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-32 h-32 bg-gradient-to-br from-teal-500 to-green-500 rounded-full flex items-center justify-center text-white text-4xl font-bold mx-auto mb-4 shadow-lg">
                  {member.initial}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">
                  {member.name}
                </h3>
                <p className="text-gray-600">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-500 to-green-500">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto text-white"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Join Our Journey
            </h2>
            <p className="text-xl text-teal-50 mb-8">
              Whether as a customer or a service professional, be part of our growing community
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-teal-600 hover:bg-gray-100 rounded-full px-10 py-6 text-lg"
                onClick={() => window.location.href = '/services'}
              >
                Book a Service
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-teal-600 rounded-full px-10 py-6 text-lg"
                onClick={() => window.location.href = '/become-worker'}
              >
                Become a Worker
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
